import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, FINANCE, REPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';

import { faDownload, faRotate } from '@fortawesome/pro-regular-svg-icons';

import * as S from '../CODReceivables/CODReceivableModal.styled';

/**
 * @author @OmerKosar
 */

const CODReceivableModal = NiceModal.create(({ ID, mode, modalName }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	const defaultColSpan = {
		md: 6
	};

	const schema = useMemo(
		() =>
			yup.object().shape({
				totalAmount: yup.number().nullable().required(),
				items: yup.array().of(
					yup.object().shape({
						otoOrderId: yup.number().nullable().required(),
						shipmentNumber: yup.string().nullable(),
						description: yup.string().nullable()
					})
				)
			}),
		[]
	);
	const fillForm = useCallback(async () => {
		const defaultValues = {
			deliveryCompId: null,
			deliveryCompName: null,
			status: null,
			startDate: null,
			endDate: null,
			totalAmount: null,
			paymentSlip: null,
			items: []
		};

		if (!ID) {
			return defaultValues;
		}

		try {
			const response = await axios.post(FINANCE.GET_COD_RECEIVABLE_DETAILS, { ID });
			const { data, status } = response;
			if (status === 200) {
				return {
					...defaultValues,
					...data
				};
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, [ID]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		control,
		clearErrors,
		setError,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, errors, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const onSubmit = async (data) => {
		try {
			const response = await axios.post(FINANCE.UPDATE_COD_RECEIVABLE_REPORT, {
				id: ID,
				amount: data.totalAmount,
				items: data.items.map((item) => ({
					id: item.id,
					amount: item.orderAmount,
					otoOrderId: item.otoOrderId,
					shipmentNumber: item.shipmentNumber,
					description: item.description
				}))
			});
			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.NEW_RECEIVABLE_REPORTS));
				showSuccess();
				modal.remove();
			}
		} catch (error) {
			if (error.response.data.success === false) {
				showError({
					message: error.response.data.reason
				});
			} else {
				showError();
				AppLogger.error(error);
			}
		}
	};

	const handleClick = async (data) => {
		try {
			const url = data;
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const deliveryCompanyIDAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	return (
		<Modal maxWidth="x-large" fullwidth name={modalName} className="modal-CODReceivableModal">
			<ModalTitle>
				<Typography variant="p1">codReceivableModal</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="deliveryCompany" isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ALL_DELIVERY_COMPANIES}
							name="deliveryCompId"
							value={watch('deliveryCompId')}
							setValue={(value) => {
								setValue('deliveryCompId', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							viewMode
							columns={deliveryCompanyIDAutocompleteColumns}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							name="status"
							enumName={ENUMS.COD_REPORT_STATUS}
							setValue={(value) => {
								setValue(`status`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`status`)}
							viewMode
						/>
					</FormItem>

					<FormItem label="startDate" colSpan={defaultColSpan} isLoading={isLoading} required>
						<DateInput
							name="startDate"
							value={watch('startDate')}
							setValue={(value) => {
								setValue('startDate', value, { shouldValidate: true, shouldDirty: true });
							}}
							viewMode
						/>
					</FormItem>
					<FormItem label="endDate" colSpan={defaultColSpan} isLoading={isLoading} required>
						<DateInput
							name="endDate"
							value={watch('endDate')}
							setValue={(value) => {
								setValue('endDate', value, { shouldValidate: true, shouldDirty: true });
							}}
							viewMode
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="totalAmount" isLoading={isLoading}>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								position: 'relative'
							}}>
							<CurrencyInput
								name="totalAmount"
								value={watch('currency') ?? 'SAR'}
								setValue={(value) => {
									setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
								}}
								currencyErrorMessage={errors?.currency?.message}
								viewMode={mode === 'view'}
							/>
							{mode !== 'view' && (
								<S.CalculateGrandTotalButton
									variant="transparent"
									icon={faRotate}
									iconButton
									size="small"
									onClick={() => {
										const controlledFields = watch('items');
										const total = controlledFields.reduce((acc, item) => acc + item.orderAmount, 0);
										setValue('totalAmount', Number(total), {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
								/>
							)}
						</div>
					</FormItem>

					{mode === 'view' && (
						<FormItem colSpan={defaultColSpan} label={'paymentSlip'}>
							{watch('paymentSlip') ? (
								<Button
									label="download"
									variant="blueBorderless"
									icon={faDownload}
									disabled={!watch('paymentSlip')}
									onClick={() => handleClick(watch('paymentSlip'))}
								/>
							) : (
								<Typography variant="p1">-</Typography>
							)}
						</FormItem>
					)}

					{mode === 'view' ? (
						<FormItem>
							<PaidCODOrdersList ID={ID} startDate={watch('startDate')} endDate={watch('endDate')} />
						</FormItem>
					) : (
						<FormItem>
							{watch('deliveryCompId') ? (
								<CODReceivableSubform
									isLoading={isLoading}
									control={control}
									clearErrors={clearErrors}
									watch={watch}
									schema={schema}
									setValue={setValue}
									setError={setError}
									mode={mode}
									deliveryCompId={watch('deliveryCompId')}
								/>
							) : null}
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					variant="outline"
					onClick={() => {
						handleCloseModal();
					}}
				/>
				{mode !== 'view' && (
					<Button
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						label="save"
						loading={isLoading}
						variant="blue"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default CODReceivableModal;

const CODReceivableSubform = ({ isLoading, control, watch, setValue, mode, deliveryCompId }) => {
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const { fields: itemsFields, append: appendItems, remove: removeItems } = useFieldArray({ control, name: 'items' });

	const removeItem = async ({ index }) => {
		removeItems(index);
	};

	const orderIDAutocompleteColumns = useMemo(() => [{ accessor: 'ID', width: 200 }], []);
	const orderIdAutocompleteLabels = useMemo(() => ['ID'], []);

	const columns = useMemo(
		() => [
			{
				id: 'otoOrderId',
				header: 'otoOrderID',
				size: isMobile ? 40 : 190,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.ORDERS_BY_DELIVERY_COMPANY}
						name={`items[${index}].otoOrderId`}
						value={watch(`items[${index}].otoOrderId`)}
						setValue={(value, isFirstRender) => {
							setValue(`items[${index}].otoOrderId`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`items[${index}].orderId`, value?.orderID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							if (!isFirstRender) {
								setValue(`items[${index}].shipmentNumber`, value?.shipmentNumber ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(`items[${index}].deliveryDate`, value?.deliveryDate ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(`items[${index}].orderAmount`, value?.amount ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(`items[${index}].orderAmountReceived`, value?.orderAmountReceived ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(`items[${index}].currency`, value?.currency ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}
						}}
						viewMode={mode === 'view'}
						disabled={deliveryCompId === null}
						columns={orderIDAutocompleteColumns}
						selectionLabelAccessor={orderIdAutocompleteLabels}
						filterObject={{
							deliveryCompId
						}}
					/>
				)
			},
			{
				header: 'orderID',
				accessorKey: 'orderId',
				size: isMobile ? 40 : 100,
				renderCell: ({ row: { index } }) => <StringInput name={`items[${index}].orderId`} viewMode />
			},
			{
				header: 'shipmentNumber',
				accessorKey: 'shipmentNumber',
				size: isMobile ? 40 : 100,
				renderCell: ({ row: { index } }) => (
					<StringInput
						name={`items[${index}].shipmentNumber`}
						viewMode={mode === 'view' || mode === 'edit'}
					/>
				)
			},

			{
				header: 'amount',
				accessorKey: 'orderAmount',
				size: 150,
				renderCell: ({ row: { index } }) => (
					<CurrencyInput
						name={`items[${index}].orderAmount`}
						value={watch(`items[${index}].currency`) ?? 'SAR'}
						setValue={(value) => {
							setValue(`items[${index}].currency`, value, { shouldValidate: true, shouldDirty: true });
						}}
						viewMode
					/>
				)
			},
			{
				header: 'amountReceived',
				accessorKey: 'orderAmountReceived',
				size: 150,
				renderCell: ({ row: { index } }) => (
					<CurrencyInput
						name={`items[${index}].orderAmountReceived`}
						value={watch(`items[${index}].currency`) ?? 'SAR'}
						setValue={(value) => {
							setValue(`items[${index}].currency`, value, { shouldValidate: true, shouldDirty: true });
						}}
						viewMode
					/>
				)
			},

			{
				header: 'description',
				accessorKey: 'description',
				renderCell: ({ row: { index } }) => (
					<StringInput name={`items[${index}].description`} viewMode={mode === 'view'} />
				)
			},
			{
				header: 'deliveryDate',
				accessorKey: 'deliveryDate',
				required: true,
				size: isMobile ? 30 : 120,
				renderCell: ({ row: { index } }) => (
					<DateInput
						name={`items[${index}].deliveryDate`}
						value={watch(`items[${index}].deliveryDate`)}
						setValue={(value) => {
							setValue(`items[${index}].deliveryDate`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						viewMode
					/>
				)
			},

			{
				header: 'receivableStatus',
				accessorKey: 'orderStatus',
				required: true,
				size: isMobile ? 30 : 120,
				renderCell: ({ row: { index } }) => <StringInput name={`items[${index}].orderStatus`} disabled={true} />
			},
			{
				header: 'payableStatus',
				accessorKey: 'payableStatus',
				required: true,
				size: isMobile ? 30 : 120,
				renderCell: ({ row: { index } }) => (
					<StringInput name={`items[${index}].payableStatus`} disabled={true} />
				)
			},
			...(mode !== 'view'
				? [
						{
							id: 'deleteRow',
							size: 30,
							maxSize: 30,
							showOnMobile: true
						}
					]
				: [])
		],
		[isMobile, deliveryCompId]
	);

	return (
		<Table
			loading={isLoading}
			data={itemsFields}
			columns={columns}
			onRowDelete={(row) => {
				removeItem(row);
			}}
			onRowAdd={() => {
				appendItems({
					id: null,
					otoOrderId: null,
					orderId: null,
					shipmentNumber: null,
					orderAmount: null,
					orderAmountReceived: null,
					description: null,
					deliveryDate: null,
					orderStatus: 'new',
					payableStatus: 'new'
				});
			}}
			deletableLastRow
			showAddNewRowButton={mode !== 'view'}
			initEditMode={(row) => row.id === null}
		/>
	);
};

const PaidCODOrdersList = ({ ID, startDate, endDate }) => {
	const { getLocaleDate } = useLocale();

	const translationObject = useMemo(
		() => ({
			startDate: getLocaleDate(startDate),
			endDate: getLocaleDate(endDate)
		}),
		[startDate, endDate]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					Header: 'receivableCODReportID',
					accessor: 'receivableCODReportID',
					filterType: FilterType.number
				},
				action: FilterAction.is,
				value: ID
			}
		],
		[]
	);

	const columns = useMemo(
		() => [
			{
				Header: 'orderID',
				accessor: 'orderIncrementID',
				filterType: FilterType.number,
				showOnMobile: true,
				width: 120
			},
			{ Header: 'shipmentNumber', accessor: 'shipmentNumber', filterType: FilterType.string },
			{ Header: 'deliveryCompany', accessor: 'dcName', filterType: FilterType.multiSelection },
			{
				Header: 'amount',
				accessor: 'amount',
				filterType: FilterType.string,
				renderCell: ListComponents.CURRENCY,
				width: 130
			},
			{
				Header: 'orderAmountReceived',
				accessor: 'orderAmountReceived',
				filterType: FilterType.string,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'deliveryDate',
				accessor: 'deliveryDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'receiveStatus',
				accessor: 'receiveStatus',
				renderCell: ListComponents.STATUS,
				filterType: FilterType.string,
				showOnMobile: true
			},
			{
				Header: 'payableStatus',
				accessor: 'status',
				renderCell: ListComponents.STATUS,
				filterType: FilterType.string,
				showOnMobile: true
			}
		],
		[]
	);

	return (
		<div>
			<DynamicList
				id={DYNAMICLISTS.RECEIVABLE_COD_DETAILS}
				columns={columns}
				fetchUrl={REPORT.LIST.GET_PAID_COD_DETAIL_LIST}
				exportName="paidCODDetailsWithDate"
				defaultFilters={defaultFilters}
				showHeader={false}
				showHeaderFilters={false}
				showFilters={false}
				exportNameTranslationObject={translationObject}
			/>
		</div>
	);
};
